import React, { useState, useEffect, useCallback, useRef } from 'react';
import AntLocalization from '../../utils/AntLocalization';
import AntCondition from '../../utils/AntCondition';
import AntTimelineItem from './AntTimelineItem';
import './index.scss';

// ─── Mock Data ───────────────────────────────────────────────────
var MOCK_ITEMS = [
    {
        id: 'mock-1', type: 'created', user: { name: 'Nguyễn Văn A' },
        timestamp: Date.now() - 3600000 * 2,
        content: { vi: 'Tạo phiếu mới', en: 'Created new voucher', fr: 'Nouveau document créé', es: 'Documento nuevo creado', id: 'Membuat dokumen baru', ja: '新規伝票を作成', zh: '创建了新单据', ko: '새 전표 생성', hi: 'नया वाउचर बनाया', ar: 'أنشأ مستندًا جديدًا' }
    },
    {
        id: 'mock-2', type: 'status_change', user: { name: 'Trần Thị B' },
        timestamp: Date.now() - 3600000,
        content: { vi: 'Đổi trạng thái', en: 'Changed status', fr: 'Statut modifié', es: 'Estado cambiado', id: 'Status diubah', ja: 'ステータスを変更', zh: '已变更状态', ko: '상태 변경됨', hi: 'स्थिति बदली', ar: 'تم تغيير الحالة' },
        meta: { from: 'New', to: 'Release' }
    },
    {
        id: 'mock-3', type: 'comment', user: { name: 'Lê Văn C' },
        timestamp: Date.now() - 1800000,
        content: 'Đã kiểm tra số liệu, mọi thứ ok.'
    },
    {
        id: 'mock-4', type: 'file_upload', user: { name: 'Nguyễn Văn A' },
        timestamp: Date.now() - 900000,
        content: { vi: 'Tải file đính kèm', en: 'Uploaded attachment', fr: 'Pièce jointe ajoutée', es: 'Adjunto subido', id: 'Mengunggah lampiran', ja: '添付をアップロード', zh: '上传了附件', ko: '첨부 파일 업로드', hi: 'अनुलग्नक अपलोड किया', ar: 'رفع مرفقًا' },
        meta: { filename: 'invoice-2026-03.pdf', url: '#' }
    },
    {
        id: 'mock-5', type: 'approval', user: { name: 'Phạm Thị D' },
        timestamp: Date.now() - 300000,
        content: { vi: 'Đã phê duyệt phiếu', en: 'Approved voucher', fr: 'Document approuvé', es: 'Documento aprobado', id: 'Menyetujui dokumen', ja: '伝票を承認', zh: '已审批单据', ko: '전표 승인', hi: 'वाउचर स्वीकृत', ar: 'اعتمد المستند' }
    },
    {
        id: 'mock-6', type: 'status_change', user: { name: 'Hệ thống' },
        timestamp: Date.now() - 60000,
        content: { vi: 'Đổi trạng thái', en: 'Changed status', fr: 'Statut modifié', es: 'Estado cambiado', id: 'Status diubah', ja: 'ステータスを変更', zh: '已变更状态', ko: '상태 변경됨', hi: 'स्थिति बदली', ar: 'تم تغيير الحالة' },
        meta: { from: 'Release', to: 'Close' }
    }
];

// ─── Date grouping helpers ──────────────────────────────────────
function toDateKey(timestamp) {
    if (!timestamp) return '';
    var ts = typeof timestamp === 'number'
        ? (timestamp < 1e12 ? timestamp * 1000 : timestamp)
        : new Date(timestamp).getTime();
    var d = new Date(ts);
    return d.toISOString().split('T')[0]; // YYYY-MM-DD
}

function getDateLabel(dateKey) {
    if (!dateKey) return '';
    var today = new Date(); today.setHours(0, 0, 0, 0);
    var d = new Date(dateKey + 'T00:00:00'); d.setHours(0, 0, 0, 0);
    var diff = Math.round((today - d) / 86400000);

    if (diff === 0) return AntLocalization.translateText({ vi: 'Hôm nay', en: 'Today', es: 'Hoy', id: 'Hari ini', ja: '今日', zh: '今天', ko: '오늘', hi: 'आज', ar: 'اليوم', fr: 'Ce jour' });
    if (diff === 1) return AntLocalization.translateText({ vi: 'Hôm qua', en: 'Yesterday', fr: 'Hier', es: 'Ayer', id: 'Kemarin', ja: '昨日', zh: '昨天', ko: '어제', hi: 'कल', ar: 'أمس' });
    if (diff <= 7) return diff + AntLocalization.translateText({ vi: ' ngày trước', en: ' days ago', fr: ' j', es: ' d', id: ' hari lalu', ja: '日前', zh: '天前', ko: '일 전', hi: ' दिन पहले', ar: ' ي' });
    return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function groupItemsByDate(items) {
    var groups = [];
    var currentKey = null;
    var currentItems = [];

    for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var key = toDateKey(item.timestamp || item.created_at);
        if (key !== currentKey) {
            if (currentItems.length > 0) groups.push({ dateKey: currentKey, label: getDateLabel(currentKey), items: currentItems });
            currentKey = key;
            currentItems = [item];
        } else {
            currentItems.push(item);
        }
    }
    if (currentItems.length > 0) groups.push({ dateKey: currentKey, label: getDateLabel(currentKey), items: currentItems });
    return groups;
}

// ─── Main Component ─────────────────────────────────────────────
export default function AntActivityTimeline(props) {
    var {
        // Data source
        items: itemsProp,
        API,
        voucherType,
        voucherCode,
        voucherId,

        // Display
        maxItems = 50,
        showLoadMore = true,
        showTimestamp = 'relative',
        groupByDate = true,
        compact = false,
        height,
        emptyText,

        // Event types override
        eventTypes,

        // Interaction
        allowComment = false,
        onCommentSubmit,
        onItemClick,

        // Filtering
        typeFilter,

        // Mock mode
        mockMode = false,

        // Visibility condition (AntCondition)
        visible,
        session
    } = props;

    var [items, setItems] = useState([]);
    var [loading, setLoading] = useState(false);
    var [hasMore, setHasMore] = useState(false);
    var [pageIndex, setPageIndex] = useState(0);
    var [commentText, setCommentText] = useState('');
    var [sending, setSending] = useState(false);
    var scrollRef = useRef(null);
    var observerRef = useRef(null);
    var loadMoreRef = useRef(null);

    // ─── Load data from API ─────────────────────────────────────
    var loadData = useCallback(async (page, append) => {
        if (!API || !voucherType) return;
        setLoading(true);
        try {
            var params = {
                voucher_type: voucherType,
                pageSize: maxItems,
                pageIndex: page
            };
            if (voucherCode) params.voucher_code = voucherCode;
            if (voucherId) params.voucher_id = voucherId;

            var result = await API.getList(params);
            var rows = (result && result.rows) || [];

            // Map ACUserComment rows to timeline items
            var mapped = rows.map(function (r) {
                return {
                    id: r.id,
                    type: 'comment',
                    user: { name: r.user_name || r.created_user_name, avatar: r.avatar_short_path || null },
                    user_name: r.user_name || r.created_user_name,
                    timestamp: r.created_at,
                    created_at: r.created_at,
                    content: r.description,
                    description: r.description,
                    _raw: r
                };
            });

            if (append) {
                setItems(function (prev) { return prev.concat(mapped); });
            } else {
                setItems(mapped);
            }
            setHasMore(rows.length >= maxItems);
        } catch (e) {
            // silent
        }
        setLoading(false);
    }, [API, voucherType, voucherCode, voucherId, maxItems]);

    // ─── Initial load ───────────────────────────────────────────
    useEffect(function () {
        if (mockMode) {
            setItems(MOCK_ITEMS);
            setHasMore(false);
        } else if (itemsProp) {
            setItems(itemsProp);
            setHasMore(false);
        } else if (API && voucherType) {
            setPageIndex(0);
            loadData(0, false);
        }
    }, [itemsProp, API, voucherType, voucherCode, voucherId, loadData, mockMode]);

    // ─── Sync itemsProp changes ─────────────────────────────────
    useEffect(function () {
        if (itemsProp) setItems(itemsProp);
    }, [itemsProp]);

    // ─── Infinite scroll (IntersectionObserver) ─────────────────
    useEffect(function () {
        if (!showLoadMore || !hasMore) return;
        var node = loadMoreRef.current;
        if (!node) return;

        var observer = new IntersectionObserver(function (entries) {
            if (entries[0].isIntersecting && hasMore && !loading) {
                var nextPage = pageIndex + 1;
                setPageIndex(nextPage);
                loadData(nextPage, true);
            }
        }, { threshold: 0.1 });

        observer.observe(node);
        observerRef.current = observer;

        return function () {
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [showLoadMore, hasMore, loading, pageIndex, loadData]);

    // ─── Comment submit ─────────────────────────────────────────
    var handleCommentSubmit = useCallback(async function () {
        var text = commentText.trim();
        if (!text) return;
        setSending(true);

        if (onCommentSubmit) {
            try {
                await onCommentSubmit(text);
                setCommentText('');
                // Reload to show new comment
                if (API && voucherType) {
                    setPageIndex(0);
                    loadData(0, false);
                }
            } catch (e) {
                // silent
            }
        }
        setSending(false);
    }, [commentText, onCommentSubmit, API, voucherType, loadData]);

    var handleCommentKeyDown = useCallback(function (e) {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleCommentSubmit();
        }
    }, [handleCommentSubmit]);

    // ─── Filter by type ─────────────────────────────────────────
    var displayItems = items;
    if (typeFilter && typeFilter.length > 0) {
        displayItems = items.filter(function (item) { return typeFilter.includes(item.type); });
    }

    // Sort newest first
    displayItems = displayItems.slice().sort(function (a, b) {
        var ta = a.timestamp || a.created_at || 0;
        var tb = b.timestamp || b.created_at || 0;
        if (typeof ta === 'string') ta = new Date(ta).getTime();
        if (typeof tb === 'string') tb = new Date(tb).getTime();
        if (typeof ta === 'number' && ta < 1e12) ta = ta * 1000;
        if (typeof tb === 'number' && tb < 1e12) tb = tb * 1000;
        return tb - ta;
    });

    // ─── Group by date ──────────────────────────────────────────
    var dateGroups = groupByDate ? groupItemsByDate(displayItems) : null;

    // ─── Empty state ────────────────────────────────────────────
    var isEmpty = displayItems.length === 0 && !loading;

    // ─── Visibility check (after all hooks) ─────────────────────
    if (visible && session && !AntCondition.checkAll({}, visible, session)) return null;

    // ─── Render ─────────────────────────────────────────────────
    return (
        <div
            className={'ant-activity-timeline' + (compact ? ' compact' : '')}
            ref={scrollRef}
            style={height ? { maxHeight: height, overflowY: 'auto' } : undefined}
        >
            {/* Comment input (top) */}
            {allowComment && (
                <div className="timeline-comment-input">
                    <textarea
                        value={commentText}
                        onChange={function (e) { setCommentText(e.target.value); }}
                        onKeyDown={handleCommentKeyDown}
                        placeholder={AntLocalization.translateText({ vi: 'Viết bình luận... (Enter để gửi)', en: 'Write a comment... (Enter to send)', fr: 'Commenter... (Entrée pour envoyer)', es: 'Escribir comentario... (Enter para enviar)', id: 'Tulis komentar... (Enter untuk kirim)', ja: 'コメントを入力...（Enterで送信）', zh: '写评论...（按 Enter 发送）', ko: '댓글 작성... (Enter로 전송)', hi: 'टिप्पणी लिखें... (भेजने हेतु Enter)', ar: 'اكتب تعليقًا... (Enter للإرسال)' })}
                        rows={1}
                        className="timeline-comment-textarea"
                    />
                    <button
                        onClick={handleCommentSubmit}
                        disabled={sending || !commentText.trim()}
                        className={'timeline-comment-btn' + (commentText.trim() ? ' active' : '')}
                    >
                        {sending ? '...' : AntLocalization.translateText({ vi: 'Gửi', en: 'Send', fr: 'Envoyer', es: 'Enviar', id: 'Kirim', ja: '送信', zh: '发送', ko: '보내기', hi: 'भेजें', ar: 'إرسال' })}
                    </button>
                </div>
            )}

            {/* Loading initial */}
            {loading && items.length === 0 && (
                <div className="timeline-loading">
                    {AntLocalization.translateText({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोड हो रहा...', ar: 'تحميل...' })}
                </div>
            )}

            {/* Empty */}
            {isEmpty && (
                <div className="timeline-empty">
                    {emptyText
                        ? (typeof emptyText === 'object' ? AntLocalization.translateText(emptyText) : emptyText)
                        : AntLocalization.translateText({ vi: 'Chưa có hoạt động', en: 'No activity yet', fr: 'Aucune activité', es: 'Sin actividad', id: 'Belum ada aktivitas', ja: 'アクティビティなし', zh: '暂无活动', ko: '활동 없음', hi: 'कोई गतिविधि नहीं', ar: 'لا يوجد نشاط' })}
                </div>
            )}

            {/* Timeline content */}
            <div className="timeline-list">
                {groupByDate && dateGroups
                    ? dateGroups.map(function (group) {
                        return (
                            <div key={group.dateKey} className="timeline-date-group">
                                <div className="timeline-date-header">
                                    <span className="timeline-date-label">{group.label}</span>
                                    <div className="timeline-date-line" />
                                </div>
                                {group.items.map(function (item) {
                                    return (
                                        <div key={item.id} onClick={onItemClick ? function () { onItemClick(item); } : undefined}>
                                            <AntTimelineItem
                                                item={item}
                                                eventTypes={eventTypes}
                                                showTimestamp={showTimestamp}
                                                compact={compact}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })
                    : displayItems.map(function (item) {
                        return (
                            <div key={item.id} onClick={onItemClick ? function () { onItemClick(item); } : undefined}>
                                <AntTimelineItem
                                    item={item}
                                    eventTypes={eventTypes}
                                    showTimestamp={showTimestamp}
                                    compact={compact}
                                />
                            </div>
                        );
                    })}
            </div>

            {/* Load more sentinel */}
            {showLoadMore && hasMore && (
                <div ref={loadMoreRef} className="timeline-load-more">
                    {loading
                        ? AntLocalization.translateText({ vi: 'Đang tải thêm...', en: 'Loading more...', fr: 'Chargement...', es: 'Cargando más...', id: 'Memuat lagi...', ja: 'さらに読み込み中...', zh: '加载更多...', ko: '더 불러오는 중...', hi: 'और लोड हो रहा...', ar: 'تحميل المزيد...' })
                        : ''}
                </div>
            )}
        </div>
    );
}
